// @ts-nocheck
// Call Stats — one Today/Yesterday/This-Week card. Purely presentational:
// the row (PeriodOverviewRow) owns which card is expanded, because the
// expansion animation moves the OTHER cards too.
import React from 'react';
import {
  FiClock,
  FiMaximize2,
  FiX,
  FiPhone,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiPhoneCall,
  FiPhoneMissed,
  FiPhoneOff,
  FiClipboard,
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { getChartColors } from './colors';
import { formatDuration } from './format';
import StatTile from './StatTile';
import { HAIRLINE, HAIRLINE_SOFT } from '@/features/cti/ctiHelpers';

const CHART_PX = 104;
// Labeled-segment ring — a hand-drawn SVG instead of ApexCharts' donut, so
// each segment's own % badge can sit right on the arc (no separate legend).
const RING_R = 40;
const RING_STROKE = 16;
const RING_CIRC = 2 * Math.PI * RING_R;

// fraction/mid-fraction are in turns (0..1), clockwise from the top.
function ringPoint(fraction, radius) {
  const angle = fraction * 2 * Math.PI;
  const center = CHART_PX / 2;
  return { x: center + radius * Math.sin(angle), y: center - radius * Math.cos(angle) };
}

function RingBadge({ x, y, color, children }: any) {
  return (
    <span
      className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[9.5px] font-bold text-white shadow-sm"
      style={{ left: x, top: y, background: color }}
    >
      {children}
    </span>
  );
}

function StatRow({ color, label, value }: any) {
  return (
    <div className="flex items-center gap-2">
      {color ? (
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />
      ) : (
        <FiClock size={11} className="shrink-0 text-muted-foreground" />
      )}
      <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">{label}</span>
      <span className="num shrink-0 text-[12px] font-semibold tabular-nums text-foreground">{value}</span>
    </div>
  );
}

export default function PeriodOverviewCard({
  period,
  isDark,
  accent,
  onChartClick,
  isActive,
  expanded = false,
  onToggleExpanded,
  loading = false,
}: any) {
  const connected = Number(period.total_connected_calls) || 0;
  const total = Number(period.total_calls) || 0;
  const notAnswered = Math.max(0, total - connected);
  const avgBilling = Number(period.avg_handle_time) || 0;
  const hasData = total > 0;
  const c = getChartColors(isDark);

  const connectedFraction = hasData ? connected / total : 0;
  const notAnsweredFraction = hasData ? notAnswered / total : 0;
  const connectedLen = connectedFraction * RING_CIRC;
  const notAnsweredLen = notAnsweredFraction * RING_CIRC;
  const connectedPct = hasData ? Math.round(connectedFraction * 1000) / 10 : 0;
  const notAnsweredPct = hasData ? Math.round(notAnsweredFraction * 1000) / 10 : 0;
  const connectedBadge = ringPoint(connectedFraction / 2, RING_R);
  const notAnsweredBadge = ringPoint(connectedFraction + notAnsweredFraction / 2, RING_R);

  return (
    // Split into an outer (shadow + hover-raise) and inner (overflow-hidden,
    // for the rounded-corner clipping the header/expanded panel need) wrapper
    // — overflow-hidden on the SAME element as a box-shadow clips that
    // shadow, which was silently eating the hover-raise effect here.
    <div
      className={`cti-kpi-card card-hover-raise h-full rounded-2xl border ${HAIRLINE} transition-shadow ${
        isActive ? 'shadow-[0_0_0_2px_hsl(var(--ring)/0.35)]' : 'shadow-sm'
      }`}
    >
    <div className="flex h-full flex-col overflow-hidden rounded-2xl bg-card">
      <div className={`flex items-center gap-2 border-b ${HAIRLINE_SOFT} px-3.5 py-2.5`}>
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: accent }} />
        <span className="flex-1 truncate text-[12px] font-semibold text-foreground">{period.label}</span>
        <button
          type="button"
          onClick={onToggleExpanded}
          title={expanded ? 'Close breakdown' : 'View breakdown'}
          aria-expanded={expanded}
          aria-label={expanded ? `Close ${period.label} breakdown` : `Open ${period.label} breakdown`}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {expanded ? <FiX size={13} /> : <FiMaximize2 size={12} />}
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-stretch">
        <div
          role="button"
          tabIndex={0}
          onClick={() => onChartClick?.(period)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onChartClick?.(period);
            }
          }}
          title={`View ${period.label} call scatter chart`}
          className={`flex cursor-pointer items-center gap-3 px-3.5 py-3 transition-colors hover:bg-muted/40 ${
            expanded ? 'shrink-0' : 'min-w-0 flex-1'
          }`}
        >
          <div
            className="relative shrink-0"
            style={{ width: CHART_PX, height: CHART_PX }}
          >
            {loading ? (
              <div
                className="animate-pulse rounded-full border-[10px] border-muted"
                style={{ width: CHART_PX - 8, height: CHART_PX - 8, margin: 4 }}
              />
            ) : (
              <>
                <svg width={CHART_PX} height={CHART_PX} viewBox={`0 0 ${CHART_PX} ${CHART_PX}`}>
                  <circle
                    cx={CHART_PX / 2}
                    cy={CHART_PX / 2}
                    r={RING_R}
                    fill="none"
                    stroke={c.emptyRing}
                    strokeWidth={RING_STROKE}
                  />
                  {hasData ? (
                    <>
                      <circle
                        cx={CHART_PX / 2}
                        cy={CHART_PX / 2}
                        r={RING_R}
                        fill="none"
                        stroke={c.outcome2[0]}
                        strokeWidth={RING_STROKE}
                        strokeDasharray={`${connectedLen} ${RING_CIRC}`}
                        transform={`rotate(-90 ${CHART_PX / 2} ${CHART_PX / 2})`}
                      />
                      <circle
                        cx={CHART_PX / 2}
                        cy={CHART_PX / 2}
                        r={RING_R}
                        fill="none"
                        stroke={c.outcome2[1]}
                        strokeWidth={RING_STROKE}
                        strokeDasharray={`${notAnsweredLen} ${RING_CIRC}`}
                        strokeDashoffset={-connectedLen}
                        transform={`rotate(-90 ${CHART_PX / 2} ${CHART_PX / 2})`}
                      />
                    </>
                  ) : null}
                  <text
                    x={CHART_PX / 2}
                    y={CHART_PX / 2 + 7}
                    textAnchor="middle"
                    fontSize="20"
                    fontWeight="700"
                    fill={accent}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {total}
                  </text>
                </svg>
                {hasData && connectedPct > 0 ? (
                  <RingBadge x={connectedBadge.x} y={connectedBadge.y} color={c.outcome2[0]}>
                    {connectedPct}%
                  </RingBadge>
                ) : null}
                {hasData && notAnsweredPct > 0 ? (
                  <RingBadge x={notAnsweredBadge.x} y={notAnsweredBadge.y} color={c.outcome2[1]}>
                    {notAnsweredPct}%
                  </RingBadge>
                ) : null}
              </>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-1.5">
            <StatRow color={c.outcome2[0]} label="Connected" value={connected} />
            <StatRow color={c.outcome2[1]} label="Not answered" value={notAnswered} />
            <StatRow label="Avg billing" value={formatDuration(avgBilling)} />
          </div>
        </div>

        {expanded ? (
          <motion.div
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.18, delay: 0.06 }}
            className={`min-w-0 flex-1 border-l ${HAIRLINE} bg-muted/20 p-3`}
          >
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
              <StatTile compact icon={FiPhone} label="Total" value={period.total_calls ?? 0} color={c.hex.call} />
              <StatTile compact icon={FiArrowDownLeft} label="Incoming" value={period.total_incoming_calls ?? 0} color={c.direction[0]} />
              <StatTile compact icon={FiArrowUpRight} label="Outgoing" value={period.total_outgoing_calls ?? 0} color={c.direction[1]} />
              <StatTile compact icon={FiPhoneCall} label="Connected" value={period.total_connected_calls ?? 0} tone="good" />
              <StatTile compact icon={FiPhoneMissed} label="Missed" value={period.total_missed_calls ?? 0} tone="warn" />
              <StatTile compact icon={FiPhoneOff} label="Declined" value={period.total_declined_calls ?? 0} color="#dc2626" />
              <StatTile
                compact
                icon={FiClock}
                label="Talk Time"
                value={formatDuration(period.total_talk_time)}
                sub={`Avg ${formatDuration(period.avg_handle_time)}`}
                color={c.periodAccents.this_week}
              />
              <StatTile
                compact
                icon={FiClipboard}
                label="Wrap Up"
                value={formatDuration(period.total_wrap_up_seconds)}
                sub={`Avg ${formatDuration(period.avg_wrap_up_seconds)}`}
                color={c.periodAccents.yesterday}
              />
            </div>
          </motion.div>
        ) : null}
      </div>
    </div>
    </div>
  );
}
